import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type { License, PaginatedResponse, Tenant, TenantStatus } from '@/types/tenant';

export const tenantKeys = {
  all: ['tenants'] as const,
  list: (page: number, pageSize: number, search: string) => [...tenantKeys.all, 'list', page, pageSize, search] as const,
  detail: (tenantId: string) => [...tenantKeys.all, 'detail', tenantId] as const,
};

export function useTenants(page: number, pageSize: number, search = '') {
  return useQuery({
    queryKey: tenantKeys.list(page, pageSize, search),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<Tenant>>('/tenants', {
        params: { page, pageSize, search: search || undefined },
      });
      return data;
    },
  });
}

export function useTenant(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.detail(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<Tenant>(`/tenants/${tenantId}`);
      return data;
    },
    enabled: Boolean(tenantId),
  });
}

export interface CreateTenantInput {
  tenantId: string;
  name: string;
  url: string;
  status?: TenantStatus;
}

export interface UpdateTenantInput {
  name?: string;
  url?: string;
  status?: TenantStatus;
}

// meta.suppressErrorToast: these mutations render their own inline error in
// the form (see TenantForm in TenantPage) — a floating toast on top of that
// would be redundant.

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateTenantInput) => {
      const { data } = await apiClient.post<Tenant>('/tenants', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
    meta: { suppressErrorToast: true },
  });
}

export function useUpdateTenant(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateTenantInput) => {
      const { data } = await apiClient.patch<Tenant>(`/tenants/${tenantId}`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
    meta: { suppressErrorToast: true },
  });
}

/** Quick inline status flip from the list table — no inline error UI of its
 * own, so a failure surfaces via the global toast instead. */
export function useToggleTenantStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, status }: { tenantId: string; status: TenantStatus }) => {
      const { data } = await apiClient.patch<Tenant>(`/tenants/${tenantId}`, { status });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
  });
}

export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      await apiClient.delete(`/tenants/${tenantId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
  });
}

export interface LicenseInput {
  endpointLimit: number;
  expiryDate: string;
  /** Only meaningful on update — issuing a license always starts at 0. */
  endpointsUsed?: number;
}

export function useIssueLicense(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: LicenseInput) => {
      const { data } = await apiClient.post<License>(`/tenants/${tenantId}/license`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
    meta: { suppressErrorToast: true },
  });
}

export function useUpdateLicense(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: Partial<LicenseInput>) => {
      const { data } = await apiClient.patch<License>(`/tenants/${tenantId}/license`, dto);
      return data;
    },
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
  });
}

/** Quick enable/disable flip — no inline error UI of its own, so a failure
 * surfaces via the global toast instead. */
export function useToggleLicenseEnabled(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data } = await apiClient.patch<License>(`/tenants/${tenantId}/license`, { enabled });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
  });
}

export function useDeleteLicense(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/tenants/${tenantId}/license`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
  });
}

export function useRegenerateLicenseKey(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<License>(`/tenants/${tenantId}/license/regenerate-key`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.all }),
  });
}
